import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Library race-bib plates — the brand IP-lock, in the three treatments the
 * validated mock uses:
 *   - stamp : filled race-yellow pill (chain labels, "N in", "Featured")
 *   - plate : filled race-yellow square (drawer hero code, e.g. S·02)
 *   - code  : dashed outline, muted (the quiet S·NN code on skill cards)
 *
 * Canonical rule (CLAUDE.md): the filled stamp is ALWAYS yellow bg + always-dark
 * ink text + dark border + dark offset shadow — it does not theme. The dark ink
 * is pinned so it stays readable even inside the OS dark scope.
 */
const INK = 'oklch(0.16 0.005 95)';

type Variant = 'stamp' | 'plate' | 'code';

export function Bib({
  children,
  variant = 'stamp',
  className,
}: {
  children: ReactNode;
  variant?: Variant;
  className?: string;
}) {
  if (variant === 'plate') {
    return (
      <span
        className={cn(
          'grid h-[46px] w-[46px] flex-none place-items-center rounded-[9px] border-[1.5px] border-fd-text bg-fd-accent font-geist-mono text-[17px] font-semibold',
          className,
        )}
        style={{ color: INK, boxShadow: '2px 2px 0 0 var(--fd-text)' }}
      >
        {children}
      </span>
    );
  }
  if (variant === 'code') {
    return (
      <span
        className={cn(
          'inline-flex flex-none place-items-center whitespace-nowrap rounded-[5px] border-[1.5px] border-dashed border-fd-border px-2 py-[2px] font-geist-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-fd-text-muted',
          className,
        )}
      >
        {children}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex flex-none place-items-center whitespace-nowrap rounded-[5px] border-[1.5px] border-fd-text bg-fd-accent px-2 py-[2px] font-geist-mono text-[10px] font-semibold uppercase tracking-[0.1em]',
        className,
      )}
      style={{ color: INK, boxShadow: '1.5px 1.5px 0 0 var(--fd-text)' }}
    >
      {children}
    </span>
  );
}
